import { useForm } from '@inertiajs/react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { OnboardingEdition } from '@/types';
import { errorMessages } from './errors';
import {
  FormItem,
  FormLabel,
  FormMessage,
  GlobalError,
} from './form-field';
import { OnboardingShell, titleFont } from './OnboardingShell';

const ANTHROPIC_PRODUCTS = [
  'Claude.ai',
  'Claude code',
  'Claude API',
  'Ninguno de los anteriores',
];

const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// anthropic-credits/_components/anthropic-credits-form.tsx (the legacy 25 flow and
// 26-ar). The two twins differ only in the subtitle and the email label.
export function AnthropicCreditsForm({
  edition,
}: {
  edition: OnboardingEdition;
}) {
  const form = useForm<{
    anthropicAccountEmail: string;
    anthropicOrgId: string;
    anthropicUsedProducts: string[];
    anthropicUpdates: boolean;
  }>({
    anthropicAccountEmail: '',
    anthropicOrgId: '',
    anthropicUsedProducts: [],
    anthropicUpdates: true,
  });
  const { data, setData, post, processing } = form;
  const errors = errorMessages(form.errors);

  const toggleProduct = (product: string) => {
    setData(
      'anthropicUsedProducts',
      data.anthropicUsedProducts.includes(product)
        ? data.anthropicUsedProducts.filter((p) => p !== product)
        : [...data.anthropicUsedProducts, product],
    );
  };

  const isValid =
    /^\S+@\S+\.\S+$/.test(data.anthropicAccountEmail) &&
    UUID_REGEX.test(data.anthropicOrgId) &&
    data.anthropicUsedProducts.length > 0;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    post(`${edition.basePath}/anthropic-credits`, { preserveScroll: true });
  };

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-2xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          Créditos{' '}
          <span className="bg-primary px-2 py-1 text-background">Anthropic</span>
        </h1>
        {edition.anthropicSubtitle && (
          <p className="mt-4 font-mono text-primary/70 text-sm">
            {edition.anthropicSubtitle}
          </p>
        )}
      </div>

      {/* Tutorial Section */}
      <div className="border-2 border-blue-500 bg-blue-50/80 p-6 backdrop-blur-sm sm:p-8">
        <div className="space-y-4">
          <p className="font-mono text-blue-800 text-sm">
            <a
              href="https://console.anthropic.com/settings/organization"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline hover:text-blue-600"
            >
              Visita console.anthropic.com/settings/organization
            </a>{' '}
            y copia los valores marcados:
          </p>
          {/* Screenshot */}
          <div className="overflow-hidden rounded-md border-2 border-blue-300">
            <a
              href="/assets/images/misc/anthropic-tutorial.png"
              target="_blank"
              rel="noopener noreferrer"
              className="block cursor-pointer transition-opacity hover:opacity-90"
            >
              <img
                src="/assets/images/misc/anthropic-tutorial.png"
                alt="Captura de pantalla mostrando cómo encontrar el Organization ID en Anthropic Console"
                width={800}
                className="h-auto w-full"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Account Email */}
          <FormItem>
            <FormLabel
              htmlFor="anthropicAccountEmail"
              error={!!errors.anthropicAccountEmail}
            >
              {edition.anthropicEmailLabel}
            </FormLabel>
            <Input
              id="anthropicAccountEmail"
              type="email"
              placeholder="tu@email.com"
              disabled={processing}
              value={data.anthropicAccountEmail}
              onChange={(e) => setData('anthropicAccountEmail', e.target.value)}
            />
            <p className="text-muted-foreground text-sm">
              Enviaremos los créditos a este email
            </p>
            <FormMessage>{errors.anthropicAccountEmail}</FormMessage>
          </FormItem>

          {/* Organization ID */}
          <FormItem>
            <FormLabel
              htmlFor="anthropicOrgId"
              error={!!errors.anthropicOrgId}
            >
              Organization ID *
            </FormLabel>
            <Input
              id="anthropicOrgId"
              placeholder="a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d"
              disabled={processing}
              value={data.anthropicOrgId}
              onChange={(e) => setData('anthropicOrgId', e.target.value)}
            />
            <p className="text-muted-foreground text-sm">
              Ingresa tu Organization ID en formato UUID
            </p>
            <FormMessage>{errors.anthropicOrgId}</FormMessage>
          </FormItem>

          {/* Used Products */}
          <FormItem>
            <FormLabel error={!!errors.anthropicUsedProducts}>
              ¿Qué productos de Anthropic has usado? *
            </FormLabel>
            <div className="space-y-2">
              {ANTHROPIC_PRODUCTS.map((product) => (
                <div key={product} className="flex items-center gap-2">
                  <Checkbox
                    id={product}
                    checked={data.anthropicUsedProducts.includes(product)}
                    onCheckedChange={() => toggleProduct(product)}
                    disabled={processing}
                  />
                  <label
                    htmlFor={product}
                    className="font-mono text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {product}
                  </label>
                </div>
              ))}
            </div>
            <FormMessage>{errors.anthropicUsedProducts}</FormMessage>
          </FormItem>

          {/* Marketing Updates */}
          <div
            data-slot="form-item"
            className="flex flex-row items-start gap-3 space-y-0"
          >
            <Checkbox
              id="anthropicUpdates"
              checked={data.anthropicUpdates}
              onCheckedChange={(checked) =>
                setData('anthropicUpdates', checked === true)
              }
              disabled={processing}
            />
            <div className="space-y-1 leading-none">
              <FormLabel htmlFor="anthropicUpdates">
                <span className="font-mono text-sm">
                  Quiero recibir actualizaciones de productos y recursos de
                  Anthropic
                </span>
              </FormLabel>
            </div>
          </div>

          {/* Global Error */}
          <GlobalError>{errors.base}</GlobalError>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              size="lg"
              disabled={processing || !isValid}
              className="w-full sm:w-auto"
            >
              {processing ? 'Guardando...' : 'Continuar'}
            </Button>
          </div>
        </form>
      </div>
    </OnboardingShell>
  );
}
